import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Heart, Shield, Award, Users, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  const coreValues = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Integrity',
      description: 'We uphold the highest ethical standards in all our business dealings and relationships.',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Safety',
      description: 'Safety is our top priority. We maintain rigorous HSE standards to protect our people and environment.',
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: 'Professionalism',
      description: 'We deliver excellence through skilled expertise, quality workmanship, and continuous improvement.',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Client Satisfaction',
      description: 'We are committed to exceeding client expectations through reliable service delivery.',
    },
  ];

  const milestones = [
    { year: '2008', event: 'Company Founded in Delta State, Nigeria' },
    { year: '2010', event: 'First Major Oil & Gas Contract Secured' },
    { year: '2014', event: 'Expanded Operations to Marine & Logistics' },
    { year: '2017', event: 'Established UK Office in London' },
    { year: '2020', event: '50+ Projects Successfully Completed' },
    { year: '2024', event: 'Leading Indigenous Engineering Firm Status' },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-[#FF6B00] text-white text-xs uppercase tracking-widest font-bold mb-6">
              About Us
            </span>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-6xl font-bold text-[#0A0A0A] uppercase leading-tight tracking-tight mb-6">
              Building Tomorrow's Energy Infrastructure
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Godston Projects Limited is an indigenous engineering firm providing comprehensive marine, logistics, procurement, oilfield, construction, and project management services.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-[4/3] bg-slate-200">
                <img 
                  src="https://images.unsplash.com/photo-1573166364839-1bfe9196c23e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBtZWV0aW5nJTIwcm9vbSUyMGRpdmVyc2UlMjB0ZWFtfGVufDB8fHx8MTc3Mzg1NDgzMnww&ixlib=rb-4.1.0&q=85"
                  alt="Corporate team meeting"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#0A0A0A] p-6 text-white hidden md:block">
                <div className="text-3xl font-bold font-['Barlow_Condensed'] text-[#FF6B00]">Since 2008</div>
                <div className="text-sm uppercase tracking-wider text-slate-400">Delivering Excellence</div>
              </div>
            </div>

            <div>
              <span className="text-[#FF6B00] text-sm uppercase tracking-widest font-bold">
                Who We Are
              </span>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#0A0A0A] uppercase tracking-tight mt-4 mb-6">
                Indigenous Engineering Excellence
              </h2>
              <div className="space-y-6 text-slate-600 leading-relaxed">
                <p>
                  Godston Projects Limited stands as a premier indigenous engineering firm, providing comprehensive solutions across the oil & gas, marine, construction, and logistics sectors. Our commitment to excellence and local content development has positioned us as a trusted partner for major industry players.
                </p>
                <p>
                  With operations spanning Nigeria and the United Kingdom, we bring international standards to local projects while maintaining the agility and responsiveness that our clients value. Our team of certified professionals combines deep industry knowledge with innovative approaches to deliver projects on time and within budget.
                </p>
                <p>
                  From wellhead maintenance to large-scale civil construction, our diverse service portfolio enables us to serve as a one-stop solution provider for all engineering needs in the oil & gas sector.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-slate-900 p-10 md:p-14 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF6B00]/10 transform rotate-45 translate-x-16 -translate-y-16" />
              <div className="relative">
                <div className="w-14 h-14 bg-[#FF6B00] flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Our Mission
                </h3>
                <p className="text-slate-400 leading-relaxed text-lg">
                  To provide effective and efficient engineering services at affordable cost while maintaining the highest professional standards, ensuring client satisfaction, and contributing to the development of local content in the oil & gas industry.
                </p>
              </div>
            </div>

            {/* Vision */}
            <div className="bg-[#FF6B00] p-10 md:p-14 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 transform rotate-45 translate-x-16 -translate-y-16" />
              <div className="relative">
                <div className="w-14 h-14 bg-white flex items-center justify-center mb-6">
                  <Eye className="w-7 h-7 text-[#FF6B00]" />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Our Vision
                </h3>
                <p className="text-white/90 leading-relaxed text-lg">
                  To be the most innovative and reliable engineering solutions provider in Sub-Saharan Africa, setting the benchmark for quality, safety, and operational excellence in the oil & gas and construction industries.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 md:py-32 bg-[#F5F5F5]" data-testid="core-values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#FF6B00] text-sm uppercase tracking-widest font-bold">
              Our Foundation
            </span>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-[#0A0A0A] uppercase tracking-tight mt-4">
              Core Values
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map((value, index) => (
              <div 
                key={index}
                className="bg-white p-8 text-center hover:shadow-lg transition-shadow"
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 bg-[#FF6B00]/10 flex items-center justify-center mx-auto mb-6">
                  <span className="text-[#FF6B00]">{value.icon}</span>
                </div>
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-[#0A0A0A] uppercase mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-32" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#FF6B00] text-sm uppercase tracking-widest font-bold">
              Our Journey
            </span>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-[#0A0A0A] uppercase tracking-tight mt-4">
              Key Milestones
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-slate-200 transform md:-translate-x-1/2" />

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div 
                  key={index}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Dot */}
                  <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-[#FF6B00] transform -translate-x-1/2 z-10" />
                  
                  {/* Content */}
                  <div className={`ml-12 md:ml-0 md:w-1/2 ${
                    index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'
                  }`}>
                    <div className="bg-white p-6 shadow-sm hover:shadow-md transition-shadow inline-block">
                      <span className="text-[#FF6B00] font-bold font-['Barlow_Condensed'] text-2xl">
                        {milestone.year}
                      </span>
                      <p className="text-slate-700 mt-2">
                        {milestone.event}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
            Partner With Us
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-10">
            Join the growing list of industry leaders who trust Godston Projects for their engineering needs.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="about-cta"
              className="bg-[#FF6B00] text-white hover:bg-[#E65000] rounded-none uppercase tracking-wider font-bold px-10 py-6 text-base btn-glow"
            >
              Get in Touch
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
